export type ClientActivitySnapshot = {
  ts_ms: number;
  path: string;
  visibility: 'visible' | 'hidden';
  auth_loading: boolean;
  has_session: boolean;
  query_total: number;
  query_active: number;
  query_fetching: number;
  mutation_active: number;
  notification_stream_state: string | null;
  used_js_heap_mb: number | null;
  total_js_heap_mb: number | null;
};

export type ClientActivityEventDetail = {
  event: string;
  detail?: Record<string, unknown>;
};

const CLIENT_ACTIVITY_SNAPSHOTS_KEY = 'flowqa-client-activity-snapshots';
const CLIENT_ACTIVITY_SAMPLE_LIMIT = 120;
export const CLIENT_ACTIVITY_EVENT_NAME = 'flowqa-client-activity';

const safeReadSnapshots = (): ClientActivitySnapshot[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(CLIENT_ACTIVITY_SNAPSHOTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is ClientActivitySnapshot =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as { ts_ms?: unknown }).ts_ms === 'number' &&
        typeof (entry as { path?: unknown }).path === 'string',
    );
  } catch {
    return [];
  }
};

const safeWriteSnapshots = (snapshots: ClientActivitySnapshot[]): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CLIENT_ACTIVITY_SNAPSHOTS_KEY, JSON.stringify(snapshots));
  } catch {
    // ignore storage failures
  }
};

export const emitClientActivityEvent = (input: ClientActivityEventDetail): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(CLIENT_ACTIVITY_EVENT_NAME, {
      detail: {
        event: input.event,
        ...(input.detail ?? {}),
        ts_ms: Date.now(),
      },
    }),
  );
};

export const recordClientActivitySnapshot = (snapshot: ClientActivitySnapshot): void => {
  if (typeof window === 'undefined') return;
  const nextSnapshots = [...safeReadSnapshots(), snapshot].slice(-CLIENT_ACTIVITY_SAMPLE_LIMIT);
  safeWriteSnapshots(nextSnapshots);
  emitClientActivityEvent({ event: 'snapshot', detail: snapshot });
};
